import React, { useState, useEffect, useCallback } from "react";
import {
  MagnifyingGlassIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  XMarkIcon,
  FunnelIcon,
  XCircleIcon,
  CheckBadgeIcon,
} from "@heroicons/react/24/outline";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import {
  getHaystackersBlogs,
  createHaystackersBlog,
  updateHaystackersBlog,
  deleteHaystackersBlog,
} from "../api/haystackersBlogs";
import { uploadFile } from "../api/blogs";
import debounce from "lodash/debounce";
import { useNavigate } from "react-router-dom";

const HaystackersBlogs = () => {
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [limit] = useState(10);
  // Initialize date range with today's date
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split("T")[0]; // Format: YYYY-MM-DD
  };

  const [dateFilterType, setDateFilterType] = useState(""); // "createdAt" or "publishedAt"
  const [dateFrom, setDateFrom] = useState(getTodayDate());
  const [selectedStatuses, setSelectedStatuses] = useState([]); // Array of selected statuses
  const [isInCarousel, setIsInCarousel] = useState(null); // null = all, true = in carousel, false = not in carousel
  const [expandedRows, setExpandedRows] = useState(new Set()); // Track expanded rows
  const [fullscreenImage, setFullscreenImage] = useState(null); // Track fullscreen image
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileError, setFileError] = useState("");
  // Validation schema (without coverImage - handled separately)
  const validationSchema = Yup.object({
    title: Yup.string().required("Title is required"),
    description: Yup.string(),
  });

  const fetchBlogs = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit,
        search,
        sortBy,
        sortOrder,
      };

      // Add status filter parameter
      if (selectedStatuses.length > 0) {
        // Join selected statuses with comma (backend supports comma-separated values)
        params.status = selectedStatuses.join(",");
      }

      // Add isInCarousel filter parameter
      if (isInCarousel !== null) {
        params.isInCarousel = isInCarousel;
      }

      // Add date filter parameters based on selected filter type
      // End date is always set to today (current date)
      // Only apply date filter if dateFilterType is selected
      if (dateFilterType === "createdAt") {
        if (dateFrom) {
          // Convert to ISO format with start of day
          const fromDate = new Date(dateFrom);
          fromDate.setHours(0, 0, 0, 0);
          params.createdAtFrom = fromDate.toISOString();
        }
        // Always set end date to today
        const today = new Date();
        today.setHours(23, 59, 59, 999);
        params.createdAtTo = today.toISOString();
      } else if (dateFilterType === "publishedAt") {
        if (dateFrom) {
          // Convert to ISO format with start of day
          const fromDate = new Date(dateFrom);
          fromDate.setHours(0, 0, 0, 0);
          params.publishedAtFrom = fromDate.toISOString();
        }
        // Always set end date to today
        const today = new Date();
        today.setHours(23, 59, 59, 999);
        params.publishedAtTo = today.toISOString();
      }

      const response = await getHaystackersBlogs(params);

      const blogsData = response.data || [];

      setBlogs(blogsData);
      setTotalPages(response.pagination?.totalPages || 1);
      setTotalItems(
        response.pagination?.totalItems ??
          response.pagination?.total ??
          0,
      );
    } catch (error) {
      toast.error(error.message || "Failed to fetch blogs");
    } finally {
      setLoading(false);
    }
  }, [
    currentPage,
    limit,
    search,
    sortBy,
    sortOrder,
    dateFilterType,
    dateFrom,
    selectedStatuses,
    isInCarousel,
  ]);

  useEffect(() => {
    const debounced = debounce(() => {
      fetchBlogs();
    }, 500);

    debounced();
    return () => {
      debounced.cancel();
    };
  }, [
    currentPage,
    search,
    sortBy,
    sortOrder,
    dateFilterType,
    dateFrom,
    selectedStatuses,
    isInCarousel,
    fetchBlogs,
  ]);

  // Debug useEffect to monitor form values
  useEffect(() => {
    console.log("Form values changed:", {
      selectedFile,
      fileError,
      imagePreview,
    });
  }, [selectedFile, fileError, imagePreview]);

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortOrder("asc");
    }
  };

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setCurrentPage(1);
  };

  const handleDateFilterTypeChange = (e) => {
    setDateFilterType(e.target.value);
    setCurrentPage(1);
  };

  const handleDateFromChange = (e) => {
    setDateFrom(e.target.value);
    setCurrentPage(1);
  };

  const handleClearDateFilter = () => {
    setDateFrom(getTodayDate());
    setCurrentPage(1);
  };

  const handleClearAllFilters = () => {
    setSelectedStatuses([]);
    setIsInCarousel(null);
    setDateFrom(getTodayDate());
    setDateFilterType("");
    setCurrentPage(1);
  };

  const hasActiveFilters = () => {
    const today = getTodayDate();
    return (
      selectedStatuses.length > 0 ||
      isInCarousel !== null ||
      (dateFilterType !== "" && dateFrom !== today)
    );
  };

  const handleStatusChange = (status) => {
    setSelectedStatuses((prev) => {
      if (prev.includes(status)) {
        // Remove status if already selected
        return prev.filter((s) => s !== status);
      } else {
        // Add status if not selected
        return [...prev, status];
      }
    });
    setCurrentPage(1);
  };

  const handleIsInCarouselChange = (value) => {
    setIsInCarousel(value);
    setCurrentPage(1);
  };

  const SortIcon = ({ column }) => {
    if (sortBy !== column) return null;
    return sortOrder === "asc" ? (
      <ChevronUpIcon className="h-4 w-4 inline-block ml-1" />
    ) : (
      <ChevronDownIcon className="h-4 w-4 inline-block ml-1" />
    );
  };

  // Function to generate slug from title
  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, "") // Remove special characters
      .replace(/\s+/g, "-") // Replace spaces with dashes
      .replace(/-+/g, "-") // Replace multiple dashes with single dash
      .trim();
  };

  const handleCreateSubmit = async (
    values,
    { setSubmitting, setFieldError, resetForm },
  ) => {
    try {
      console.log("Form values:", values);
      console.log("Selected file:", selectedFile);

      // Validate file separately
      if (!selectedFile || !validateFile(selectedFile)) {
        setFieldError(
          "coverImage",
          fileError || "Please select a valid image file",
        );
        return;
      }

      const slug = generateSlug(values.title);

      const uploadResult = await uploadFile(
        selectedFile,
        "uploads/blogs",
        true,
      );

      const blogData = {
        title: values.title,
        description: values.description || "",
        coverImagePath: uploadResult.data.uploadUrl,
        slug: slug,
        status: "DRAFT",
        tags: [],
        companyId: "64a1b2c3d4e5f6789012345", // You may want to get this from context or props
        seoTitle: values.seoTitle || "",
        seoKeywords: values.seoKeywords || "",
      };

      await createHaystackersBlog(blogData);
      toast.success("Blog created successfully");
      setIsCreateModalOpen(false);
      setImagePreview(null);
      setSelectedFile(null);
      setFileError("");
      resetForm();
      fetchBlogs();
    } catch (error) {
      toast.error(error.message || "Failed to create blog");
      setFieldError("submit", "Failed to create blog");
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateSubmit = async (
    values,
    { setSubmitting, setFieldError },
  ) => {
    try {
      // Keep the original slug in edit mode - don't regenerate it
      const slug = selectedBlog.slug;

      let coverImagePath = selectedBlog.coverImagePath; // Keep existing image path

      // If a new file is uploaded, upload it first
      if (selectedFile && validateFile(selectedFile)) {
        const uploadResult = await uploadFile(
          selectedFile,
          "uploads/blogs",
          true,
        );
        coverImagePath = uploadResult.data.uploadUrl;
      }

      const blogData = {
        title: values.title,
        description: values.description || "",
        coverImagePath: coverImagePath,
        slug: slug, // Keep original slug
        status: selectedBlog.status || "DRAFT",
        tags: selectedBlog.tags || [],
        companyId: selectedBlog.companyId || "64a1b2c3d4e5f6789012345",
        seoTitle: values.seoTitle || "",
        seoKeywords: values.seoKeywords || "",
      };

      await updateHaystackersBlog(selectedBlog._id, blogData);
      toast.success("Blog updated successfully");
      setIsEditModalOpen(false);
      setImagePreview(null);
      setSelectedFile(null);
      setFileError("");
      fetchBlogs();
    } catch (error) {
      toast.error(error.message || "Failed to update blog");
      setFieldError("submit", "Failed to update blog");
    } finally {
      setSubmitting(false);
    }
  };

  // Separate file validation function
  const validateFile = (file) => {
    if (!file) {
      setFileError("Please select a file");
      return false;
    }

    if (!(file instanceof File)) {
      setFileError("Please select a valid file");
      return false;
    }

    if (file.size > 5 * 1024 * 1024) {
      setFileError("File size must be less than 5MB");
      return false;
    }

    const allowedTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/gif",
      "image/webp",
    ];
    if (!allowedTypes.includes(file.type)) {
      setFileError("Only image files are allowed (JPEG, PNG, GIF, WebP)");
      return false;
    }

    setFileError("");
    return true;
  };

  const handleFileChange = (file) => {
    console.log("File selected:", file);
    console.log("Current selectedFile before update:", selectedFile);
    setSelectedFile(file);
    console.log("selectedFile state updated");

    if (file && validateFile(file)) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    } else if (!file) {
      if (selectedBlog && selectedBlog.coverImagePath) {
        setImagePreview(selectedBlog.coverImagePath);
      } else {
        setImagePreview(null);
      }
    } else {
      setImagePreview(null);
    }
  };

  const handleEditClick = (blog) => {
    setSelectedBlog(blog);
    if (blog.coverImagePath) {
      setImagePreview(blog.coverImagePath);
    } else {
      setImagePreview(null);
    }
    // Clear any previously selected file
    setSelectedFile(null);
    setFileError("");
    setIsEditModalOpen(true);
  };

  const handleBlogClick = (blogId) => {
    console.log(blogId);
    navigate(`/dashboard/haystackers-blogs-details/${blogId}`);
  };

  const toggleRowExpansion = (blogId) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(blogId)) {
        newSet.delete(blogId);
      } else {
        newSet.add(blogId);
      }
      return newSet;
    });
  };

  const handleImageClick = (e, imageUrl) => {
    e.stopPropagation();
    setFullscreenImage(imageUrl);
  };

  const closeFullscreenImage = () => {
    setFullscreenImage(null);
  };

  // Handle ESC key to close fullscreen image
  useEffect(() => {
    if (!fullscreenImage) return;

    const handleEscKey = (event) => {
      if (event.key === "Escape") {
        setFullscreenImage(null);
      }
    };
    window.addEventListener("keydown", handleEscKey);
    return () => {
      window.removeEventListener("keydown", handleEscKey);
    };
  }, [fullscreenImage]);

  const handleDeleteClick = async (blog) => {
    if (window.confirm(`Are you sure you want to delete "${blog.title}"?`)) {
      try {
        await deleteHaystackersBlog(blog._id);
        toast.success("Blog deleted successfully");
        fetchBlogs();
      } catch (error) {
        toast.error(error.message || "Failed to delete blog");
      }
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const BlogModal = ({ isOpen, onClose, onSubmit, initialValues, title }) => {
    if (!isOpen) return null;

    return (
      <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] flex flex-col shadow-xl my-auto">
          {/* Fixed Header */}
          <div className="flex justify-between items-center flex-shrink-0 px-6 pt-6 pb-4 border-b border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Scrollable Body */}
          <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4">
          {/* File input outside of Formik */}
          <div className="mb-6">
            <label
              htmlFor="coverImage"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Cover Image *
            </label>
            <div className="flex gap-2">
              <input
                type="file"
                id="coverImage"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files[0];
                  handleFileChange(file);
                }}
                className={`flex-1 px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:border-transparent sm:text-sm ${
                  fileError
                    ? "border-red-300 focus:ring-red-500"
                    : "border-gray-300 focus:ring-primary"
                }`}
              />
              {selectedFile && (
                <button
                  type="button"
                  onClick={() => {
                    document.getElementById("coverImage").value = "";
                    handleFileChange(null);
                  }}
                  className="px-3 py-2 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Clear
                </button>
              )}
            </div>
            {imagePreview && (
              <div className="mt-2">
                <img
                  src={imagePreview}
                  alt="Preview"
                  className="h-32 w-32 object-cover rounded-lg border"
                />
                {selectedFile && (
                  <p className="mt-1 text-xs text-blue-600">
                    New file selected - will replace current image
                  </p>
                )}
              </div>
            )}
            {fileError && (
              <p className="mt-2 text-sm text-red-600">{fileError}</p>
            )}
            <p className="mt-1 text-xs text-gray-500">
              Supported formats: JPEG, PNG, GIF, WebP. Max size: 5MB
            </p>
            {selectedFile ? (
              <p className="mt-1 text-xs text-green-600">
                New file selected: {selectedFile.name} (
                {(selectedFile.size / 1024).toFixed(1)} KB)
              </p>
            ) : selectedBlog && selectedBlog.coverImagePath ? (
              <p className="mt-1 text-xs text-gray-600">
                Using existing image:{" "}
                {selectedBlog.coverImagePath.split("/").pop()}
              </p>
            ) : null}
          </div>

          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={onSubmit}
            enableReinitialize
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              isSubmitting,
              setFieldValue,
            }) => {
              return (
                <Form className="flex flex-col flex-1 min-h-0">
                  <div className="flex-1 min-h-0 overflow-y-auto space-y-5">
                  <div>
                    <label
                      htmlFor="title"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Title *
                    </label>
                    <input
                      type="text"
                      id="title"
                      name="title"
                      value={values.title}
                      onChange={(e) => {
                        handleChange(e);
                        // Auto-generate slug when title changes
                        if (e.target.value && !selectedBlog) {
                          setFieldValue("slug", generateSlug(e.target.value));
                        }
                      }}
                      onBlur={handleBlur}
                      className={`block w-full px-3 py-2 border rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-transparent sm:text-sm ${
                        errors.title && touched.title
                          ? "border-red-300 focus:ring-red-500"
                          : "border-gray-300 focus:ring-primary"
                      }`}
                      placeholder="Enter blog title"
                    />
                    {errors.title && touched.title && (
                      <p className="mt-2 text-sm text-red-600">
                        {errors.title}
                      </p>
                    )}
                  </div>

                  <div>
                    <label
                      htmlFor="slug"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Slug (Auto-generated)
                    </label>
                    <input
                      type="text"
                      id="slug"
                      name="slug"
                      value={values.slug}
                      readOnly
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-50 text-gray-600 sm:text-sm"
                      placeholder="Slug will be generated automatically"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      This will be auto-generated from the title
                    </p>
                  </div>

                  <div>
                    <label
                      htmlFor="description"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Description (Optional)
                    </label>
                    <textarea
                      id="description"
                      name="description"
                      value={values.description}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      rows={4}
                      className={`block w-full px-3 py-2 border rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-transparent sm:text-sm ${
                        errors.description && touched.description
                          ? "border-red-300 focus:ring-red-500"
                          : "border-gray-300 focus:ring-primary"
                      }`}
                      placeholder="Enter blog description (optional)"
                    />
                    {errors.description && touched.description && (
                      <p className="mt-2 text-sm text-red-600">
                        {errors.description}
                      </p>
                    )}
                  </div>

                  <div>
                    <label
                      htmlFor="seoTitle"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      SEO Title (Optional)
                    </label>
                    <input
                      type="text"
                      id="seoTitle"
                      name="seoTitle"
                      value={values.seoTitle || ""}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={`block w-full px-3 py-2 border rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-transparent sm:text-sm ${
                        errors.seoTitle && touched.seoTitle
                          ? "border-red-300 focus:ring-red-500"
                          : "border-gray-300 focus:ring-primary"
                      }`}
                      placeholder="Enter SEO title (optional)"
                    />
                    {errors.seoTitle && touched.seoTitle && (
                      <p className="mt-2 text-sm text-red-600">
                        {errors.seoTitle}
                      </p>
                    )}
                    <p className="mt-1 text-xs text-gray-500">
                      Optimize your blog title for search engines
                    </p>
                  </div>

                  <div>
                    <label
                      htmlFor="seoKeywords"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      SEO Keywords (Optional)
                    </label>
                    <input
                      type="text"
                      id="seoKeywords"
                      name="seoKeywords"
                      value={values.seoKeywords || ""}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={`block w-full px-3 py-2 border rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-transparent sm:text-sm ${
                        errors.seoKeywords && touched.seoKeywords
                          ? "border-red-300 focus:ring-red-500"
                          : "border-gray-300 focus:ring-primary"
                      }`}
                      placeholder="Enter SEO keywords separated by commas (optional)"
                    />
                    {errors.seoKeywords && touched.seoKeywords && (
                      <p className="mt-2 text-sm text-red-600">
                        {errors.seoKeywords}
                      </p>
                    )}
                    <p className="mt-1 text-xs text-gray-500">
                      Enter keywords separated by commas (e.g., keyword1,
                      keyword2, keyword3)
                    </p>
                  </div>

                  {errors.submit && (
                    <div className="rounded-md bg-red-50 p-4">
                      <div className="text-sm text-red-700">
                        {errors.submit}
                      </div>
                    </div>
                  )}

                  </div>

                  {/* Fixed Footer */}
                  <div className="flex-shrink-0 flex justify-end space-x-3 px-6 py-4 border-t border-gray-100 bg-white rounded-b-lg">
                    <button
                      type="button"
                      onClick={onClose}
                      className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="flex justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? (
                        <div className="flex items-center">
                          <svg
                            className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          Saving...
                        </div>
                      ) : title.includes("Create") ? (
                        "Create Haystackers Blog"
                      ) : (
                        "Update Haystackers Blog"
                      )}
                    </button>
                  </div>
                </Form>
              );
            }}
          </Formik>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Haystackers Blogs</h1>
        <div className="flex items-center space-x-4">
          <button
            onClick={() => {
              setSelectedBlog(null);
              setIsCreateModalOpen(true);
            }}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Add Blog
          </button>
          <div className="relative">
            <input
              type="text"
              placeholder="Search blogs..."
              value={search}
              onChange={handleSearch}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-2.5" />
          </div>
        </div>
      </div>

      {/* Filters Section */}
      <div className="mb-6 bg-white rounded-lg shadow-sm border border-gray-200">
        {/* Filter Header */}
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <FunnelIcon className="h-5 w-5 text-gray-500" />
            <h2 className="text-lg font-semibold text-gray-900">Filters</h2>
            {hasActiveFilters() && (
              <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
                {selectedStatuses.length +
                  (isInCarousel !== null ? 1 : 0) +
                  (dateFilterType !== "" ? 1 : 0)}{" "}
                active
              </span>
            )}
          </div>
          {(hasActiveFilters() || dateFilterType !== "") && (
            <button
              onClick={handleClearAllFilters}
              className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors"
            >
              <XCircleIcon className="h-4 w-4 mr-1.5" />
              Clear All
            </button>
          )}
        </div>

        {/* Filter Content */}
        <div className="p-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 items-end">
            {/* Status Filter */}
            <div className="flex flex-col gap-2 min-w-0">
              <label className="text-sm font-semibold text-gray-700">
                Status
              </label>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer group">
                  <span
                    className="relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus-within:ring-2 focus-within:ring-primary focus-within:ring-offset-2 bg-gray-200"
                    style={{ backgroundColor: selectedStatuses.includes("DRAFT") ? "var(--primary, #000)" : undefined }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedStatuses.includes("DRAFT")}
                      onChange={() => handleStatusChange("DRAFT")}
                      className="sr-only"
                    />
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                        selectedStatuses.includes("DRAFT") ? "translate-x-5" : "translate-x-0.5"
                      }`}
                    />
                  </span>
                  <span className="text-sm text-gray-700 group-hover:text-gray-900">
                    Draft
                  </span>
                  {selectedStatuses.includes("DRAFT") && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                      DRAFT
                    </span>
                  )}
                </label>
                <label className="flex items-center gap-2 cursor-pointer group">
                  <span
                    className="relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus-within:ring-2 focus-within:ring-primary focus-within:ring-offset-2 bg-gray-200"
                    style={{ backgroundColor: selectedStatuses.includes("PUBLISHED") ? "var(--primary, #000)" : undefined }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedStatuses.includes("PUBLISHED")}
                      onChange={() => handleStatusChange("PUBLISHED")}
                      className="sr-only"
                    />
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                        selectedStatuses.includes("PUBLISHED") ? "translate-x-5" : "translate-x-0.5"
                      }`}
                    />
                  </span>
                  <span className="text-sm text-gray-700 group-hover:text-gray-900">
                    Published
                  </span>
                  {selectedStatuses.includes("PUBLISHED") && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                      PUBLISHED
                    </span>
                  )}
                </label>
              </div>
            </div>

            {/* In Carousel Filter */}
            <div className="flex flex-col gap-2 min-w-0">
              <label className="text-sm font-semibold text-gray-700">
                In Carousel
              </label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    // Cycle through: null (All) -> true (Yes) -> false (No) -> null (All)
                    if (isInCarousel === null) {
                      handleIsInCarouselChange(true);
                    } else if (isInCarousel === true) {
                      handleIsInCarouselChange(false);
                    } else {
                      handleIsInCarouselChange(null);
                    }
                  }}
                  className={`relative inline-flex h-7 w-14 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
                    isInCarousel === true
                      ? "bg-green-500"
                      : isInCarousel === false
                        ? "bg-red-500"
                        : "bg-gray-300"
                  }`}
                  role="switch"
                  aria-checked={isInCarousel !== null}
                >
                  <span
                    className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out ${
                      isInCarousel === null
                        ? "translate-x-0.5"
                        : "translate-x-7"
                    }`}
                  />
                </button>
                <span className="text-sm text-gray-700 whitespace-nowrap">
                  {isInCarousel === null ? "All" : isInCarousel ? "In Carousel" : "Not in Carousel"}
                </span>
              </div>
            </div>

            {/* Date Range Filter */}
            <div className="flex flex-col gap-2 min-w-0">
              <label className="text-sm font-semibold text-gray-700">
                Date
              </label>
              <div className="flex flex-wrap items-center gap-2">
                <select
                  id="dateFilterType"
                  value={dateFilterType}
                  onChange={handleDateFilterTypeChange}
                  className="min-w-[140px] px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm bg-white"
                >
                  <option value="">Date type</option>
                  <option value="createdAt">Created</option>
                  <option value="publishedAt">Published</option>
                </select>
                {dateFilterType && (
                  <>
                    <input
                      type="date"
                      id="dateFrom"
                      value={dateFrom}
                      onChange={handleDateFromChange}
                      max={getTodayDate()}
                      className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                    />
                    {dateFrom !== getTodayDate() && (
                      <button
                        type="button"
                        onClick={handleClearDateFilter}
                        className="px-2 py-1.5 text-xs font-medium text-gray-600 bg-gray-50 border border-gray-300 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                      >
                        Reset
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Active Filters Badges */}
          {hasActiveFilters() && (
            <div className="mt-5 pt-5 border-t border-gray-200">
              <div className="flex items-center flex-wrap gap-2">
                <span className="text-xs font-medium text-gray-500">
                  Active Filters:
                </span>
                {selectedStatuses.map((status) => (
                  <span
                    key={status}
                    className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary border border-primary/20"
                  >
                    Status: {status}
                    <button
                      onClick={() => handleStatusChange(status)}
                      className="ml-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full hover:bg-primary/20 focus:outline-none"
                    >
                      <XMarkIcon className="h-3 w-3" />
                    </button>
                  </span>
                ))}
                {isInCarousel !== null && (
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary border border-primary/20">
                    In Carousel: {isInCarousel ? "Yes" : "No"}
                    <button
                      onClick={() => handleIsInCarouselChange(null)}
                      className="ml-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full hover:bg-primary/20 focus:outline-none"
                    >
                      <XMarkIcon className="h-3 w-3" />
                    </button>
                  </span>
                )}
                {dateFilterType !== "" && (
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary border border-primary/20">
                    {dateFilterType === "createdAt" ? "Created" : "Published"}{" "}
                    Date: {dateFrom} - {getTodayDate()}
                    <button
                      onClick={() => {
                        setDateFilterType("");
                        setDateFrom(getTodayDate());
                      }}
                      className="ml-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full hover:bg-primary/20 focus:outline-none"
                    >
                      <XMarkIcon className="h-3 w-3" />
                    </button>
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Blogs Table */}
      <div className="bg-white rounded-lg shadow">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-10"></th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                  Image
                </th>
                <th
                  className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer min-w-[300px]"
                  onClick={() => handleSort("title")}
                >
                  Title <SortIcon column="title" />
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  In Carousel
                </th>
                <th
                  className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort("createdAt")}
                >
                  Created At <SortIcon column="createdAt" />
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td
                    colSpan={7}
                    className="px-6 py-4 text-center text-sm text-gray-500"
                  >
                    Loading...
                  </td>
                </tr>
              ) : blogs.length === 0 ? (
                <tr>
                  <td
                    colSpan={7}
                    className="px-6 py-4 text-center text-sm text-gray-500"
                  >
                    No blogs found
                  </td>
                </tr>
              ) : (
                blogs.map((blog) => {
                  const isExpanded = expandedRows.has(blog._id);
                  return (
                    <React.Fragment key={blog._id}>
                      <tr className="hover:bg-gray-50">
                        <td className="px-2 py-2 whitespace-nowrap">
                          <button
                            onClick={() => toggleRowExpansion(blog._id)}
                            className="p-1 hover:bg-gray-200 rounded transition-colors"
                            aria-label={isExpanded ? "Collapse" : "Expand"}
                          >
                            {isExpanded ? (
                              <ChevronDownIcon className="h-4 w-4 text-gray-500" />
                            ) : (
                              <ChevronRightIcon className="h-4 w-4 text-gray-500" />
                            )}
                          </button>
                        </td>
                        <td className="px-2 py-2 w-20">
                          {blog.coverImagePath ? (
                            <img
                              src={blog.coverImagePath}
                              alt={blog.title}
                              className="h-10 w-10 object-cover rounded cursor-pointer hover:opacity-80 transition-opacity"
                              onClick={(e) =>
                                handleImageClick(e, blog.coverImagePath)
                              }
                              onError={(e) => {
                                console.error(
                                  "Image failed to load:",
                                  blog.coverImagePath,
                                );
                                e.target.style.display = "none";
                              }}
                            />
                          ) : (
                            <div className="h-10 w-10 bg-gray-200 rounded-lg flex items-center justify-center">
                              <span className="text-gray-400 text-xs">
                                No Image
                              </span>
                            </div>
                          )}
                        </td>
                        <td className="px-2 py-2 min-w-[300px] max-w-[400px]">
                          <a
                            href={`/dashboard/haystackers-blogs-details/${blog._id}`}
                            onClick={(e) => {
                              e.preventDefault();
                              handleBlogClick(blog._id);
                            }}
                            className="text-sm font-medium text-primary hover:text-primary-dark hover:underline block"
                            style={{
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {blog.title}
                          </a>
                        </td>
                        <td className="px-2 py-2 whitespace-nowrap">
                          <div
                            className={`${
                              blog.status === "DRAFT"
                                ? "bg-yellow-100 text-yellow-800"
                                : "bg-green-100 text-green-800"
                            } px-2 inline-flex text-xs leading-5 font-semibold rounded-full`}
                          >
                            {blog.status}
                          </div>
                        </td>
                        <td className="px-2 py-2 whitespace-nowrap">
                          <div className="text-xs text-gray-500">
                            {blog.isInCarousel ? (
                              <CheckBadgeIcon className="h-5 w-5 text-green-500" />
                            ) : (
                              <XCircleIcon className="h-5 w-5 text-red-500" />
                            )}
                          </div>
                        </td>
                        <td className="px-2 py-2 whitespace-nowrap">
                          <div className="text-xs text-gray-500">
                            {formatDate(blog.createdAt)}
                          </div>
                        </td>
                        <td className="px-2 py-2 whitespace-nowrap text-sm text-gray-500">
                          <div className="flex justify-end space-x-1">
                            <button
                              onClick={() => handleEditClick(blog)}
                              className="inline-flex items-center p-1.5 border border-transparent rounded-md text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                              title="Edit Blog"
                            >
                              <PencilSquareIcon className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteClick(blog)}
                              className="inline-flex items-center p-1.5 border border-transparent rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                              title="Delete Blog"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className="bg-gray-50">
                          <td colSpan={7} className="px-3 py-3">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              <div>
                                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                  Description
                                </label>
                                <p className="mt-1 text-sm text-gray-900">
                                  {blog.description || "No description"}
                                </p>
                              </div>
                              <div>
                                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                  Slug
                                </label>
                                <p className="mt-1 text-sm text-gray-900 font-mono">
                                  {blog.slug}
                                </p>
                              </div>
                              <div>
                                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                  Created By
                                </label>
                                <p className="mt-1 text-sm text-gray-900">
                                  {blog?.author?.name || "N/A"}
                                </p>
                              </div>
                              <div>
                                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                  Published At
                                </label>
                                <p className="mt-1 text-sm text-gray-900">
                                  {blog.publishedAt
                                    ? formatDate(blog.publishedAt)
                                    : "Not published"}
                                </p>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Showing{" "}
                <span className="font-medium">
                  {(currentPage - 1) * limit + 1}
                </span>{" "}
                to{" "}
                <span className="font-medium">
                  {Math.min(currentPage * limit, totalItems)}
                </span>{" "}
                of <span className="font-medium">{totalItems}</span> results
              </p>
            </div>
            <div>
              <nav
                className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
                aria-label="Pagination"
              >
                <button
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                {[...Array(totalPages)].map((_, index) => (
                  <button
                    key={index + 1}
                    onClick={() => setCurrentPage(index + 1)}
                    className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                      currentPage === index + 1
                        ? "z-10 bg-primary border-primary text-white"
                        : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                    }`}
                  >
                    {index + 1}
                  </button>
                ))}
                <button
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </nav>
            </div>
          </div>
        </div>
      </div>

      {/* Create Modal */}
      <BlogModal
        isOpen={isCreateModalOpen}
        onClose={() => {
          setIsCreateModalOpen(false);
          setImagePreview(null);
          setSelectedFile(null);
          setFileError("");
          setSelectedBlog(null);
        }}
        onSubmit={handleCreateSubmit}
        initialValues={{
          title: "",
          description: "",
          slug: "",
          seoTitle: "",
          seoKeywords: "",
        }}
        title="Create Haystackers Blog"
      />

      {/* Edit Modal */}
      <BlogModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setImagePreview(null);
          setSelectedFile(null);
          setFileError("");
          setSelectedBlog(null);
        }}
        onSubmit={handleUpdateSubmit}
        initialValues={{
          title: selectedBlog?.title || "",
          description: selectedBlog?.description || "",
          slug: selectedBlog?.slug || "",
          seoTitle: selectedBlog?.seoTitle || "",
          seoKeywords: selectedBlog?.seoKeywords || "",
        }}
        title="Edit Haystackers Blog"
      />

      {/* Fullscreen Image Modal */}
      {fullscreenImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4"
          onClick={closeFullscreenImage}
        >
          <button
            onClick={closeFullscreenImage}
            className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors"
            aria-label="Close"
          >
            <XMarkIcon className="h-8 w-8" />
          </button>
          <img
            src={fullscreenImage}
            alt="Fullscreen"
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default HaystackersBlogs;
